import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../api';
import './Advertise.css';

function useAdvertisingInventory(retryKey) {
  const [state, setState] = useState({ packages: [], inventory: [], loading: true, error: null });

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setState((current) => ({ ...current, loading: true, error: null }));
        const [packagesResponse, inventoryResponse] = await Promise.all([
          fetch(`${API_URL}/directory/packages`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal }),
          fetch(`${API_URL}/directory/inventory`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal }),
        ]);

        if (!packagesResponse.ok || !inventoryResponse.ok) {
          throw new Error('Advertising inventory is temporarily unavailable.');
        }

        const [packageBody, inventoryBody] = await Promise.all([
          packagesResponse.json(),
          inventoryResponse.json(),
        ]);

        setState({
          packages: Array.isArray(packageBody.data) ? packageBody.data : [],
          inventory: Array.isArray(inventoryBody.data) ? inventoryBody.data : [],
          loading: false,
          error: null,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          setState((current) => ({ ...current, loading: false, error }));
        }
      }
    };

    load();
    return () => controller.abort();
  }, [retryKey]);

  return state;
}

export default function Advertise() {
  const [retryKey, setRetryKey] = useState(0);
  const { packages, inventory, loading, error } = useAdvertisingInventory(retryKey);
  const availableByKey = useMemo(
    () => new Map(inventory.map((item) => [item.key, item.available])),
    [inventory]
  );
  const availableSpaces = inventory.reduce((total, item) => total + Math.max(0, Number(item.available) || 0), 0);

  return (
    <main className="advertise-page">
      <section className="advertise-hero" aria-labelledby="advertise-title">
        <div className="advertise-hero__glow" aria-hidden="true" />
        <div className="advertise-shell advertise-hero__content">
          <p className="advertise-kicker">Advertise with Guhso</p>
          <h1 id="advertise-title">Put your business in front of <span>the right people.</span></h1>
          <p className="advertise-hero__lede">Choose a high-attention placement, reserve live inventory, and build a polished campaign for the Guhso community.</p>
          <div className="advertise-hero__actions">
            <a href="#advertising-packages">View packages <i className="fas fa-arrow-down" aria-hidden="true" /></a>
            <Link to="/directory">See the Directory</Link>
          </div>
          <dl className="advertise-stats" aria-label="Advertising overview">
            <div><dt>{loading ? '—' : packages.length}</dt><dd>Live packages</dd></div>
            <div><dt>{loading ? '—' : availableSpaces}</dt><dd>Spaces available</dd></div>
            <div><dt>30</dt><dd>Day availability window</dd></div>
          </dl>
        </div>
      </section>

      <section id="advertising-packages" className="advertise-packages" aria-labelledby="advertise-packages-title">
        <div className="advertise-shell">
          <div className="advertise-section-heading">
            <div><p>Transparent inventory</p><h2 id="advertise-packages-title">Choose the attention you need</h2></div>
            <span><i className="fas fa-circle" aria-hidden="true" /> Live 30-day availability</span>
          </div>

          {loading && <PackageSkeleton />}
          {!loading && error && (
            <div className="advertise-state" role="alert">
              <i className="fas fa-satellite-dish" aria-hidden="true" />
              <h3>We could not load live inventory.</h3>
              <p>Please try again. No campaign selection has been made.</p>
              <button type="button" onClick={() => setRetryKey((value) => value + 1)}>Try again</button>
            </div>
          )}
          {!loading && !error && packages.length === 0 && (
            <div className="advertise-state"><h3>New packages are coming soon.</h3><p>Check back for the next available campaign window.</p></div>
          )}
          {!loading && !error && packages.length > 0 && (
            <div className="advertise-package-grid">
              {packages.map((item) => (
                <PackageCard key={item.id} item={item} available={availableByKey.get(item.placement?.key)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="advertise-process" aria-labelledby="advertise-process-title">
        <div className="advertise-shell">
          <div className="advertise-section-heading"><div><p>Simple campaign setup</p><h2 id="advertise-process-title">From idea to audience</h2></div></div>
          <ol>
            <li><span>01</span><div><h3>Choose your placement</h3><p>Compare live availability, campaign length, and included features.</p></div></li>
            <li><span>02</span><div><h3>Build your campaign</h3><p>Select an existing listing or submit a new business and its creative.</p></div></li>
            <li><span>03</span><div><h3>Review and launch</h3><p>Your campaign enters creative review before its approved schedule begins.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="advertise-final-cta">
        <div className="advertise-shell"><div><p className="advertise-kicker">Ready when you are</p><h2>Make your next impression count.</h2></div><a href="#advertising-packages">Choose a package <i className="fas fa-arrow-up" aria-hidden="true" /></a></div>
      </section>
    </main>
  );
}

function PackageCard({ item, available }) {
  const isSoldOut = available === 0;
  const features = Array.isArray(item.features) ? item.features : [];
  const price = Number(item.price);

  return (
    <article className={`advertise-package${isSoldOut ? ' is-sold-out' : ''}`}>
      <header><p>{item.placement?.name || 'Guhso placement'}</p><h3>{item.name}</h3></header>
      <p className="advertise-package__price">{Number.isFinite(price) ? `$${price.toLocaleString()}` : 'Contact us'} <small>/ {item.durationDays || 30} days</small></p>
      {features.length > 0 && <ul>{features.map((feature) => <li key={feature}><i className="fas fa-check" aria-hidden="true" /> {feature}</li>)}</ul>}
      <div className={`advertise-availability${isSoldOut ? ' is-sold-out' : ''}`}>
        <span aria-hidden="true" />
        {available === undefined ? 'Check availability' : isSoldOut ? 'Currently sold out' : `${available} ${available === 1 ? 'space' : 'spaces'} available`}
      </div>
      {isSoldOut
        ? <span className="advertise-package__disabled" aria-disabled="true">Sold out</span>
        : <Link to={`/advertise/checkout?package=${encodeURIComponent(item.slug)}`}>Build this campaign <i className="fas fa-arrow-right" aria-hidden="true" /></Link>}
    </article>
  );
}

function PackageSkeleton() {
  return <div className="advertise-package-grid" aria-label="Loading advertising packages">{[1, 2, 3].map((item) => <div key={item} className="advertise-package-skeleton" />)}</div>;
}
